// PDF renderers for the /compare page: SumatraPDF (its wasm build in
// ?render-api mode, in a hidden iframe), and the latest released mupdf, pdfium
// and pdf.js, which -update-deps puts in /engines.
//
// mupdf and pdfium run in workers (public/compare/*-worker.js). Messages to a
// worker: { id, type: "open", bytes } answered with { id, pageCount }, and
// { id, type: "render", pageNo, dx, dy } answered with { id, width, height,
// pixels } (RGBA). A failure is { id, error }. A worker sends { ready: true }
// once it can take messages.

export type EngineId = "sumatra" | "mupdf" | "pdfium" | "pdfjs";

export const ENGINE_IDS: EngineId[] = ["sumatra", "mupdf", "pdfium", "pdfjs"];

export const ENGINE_NAMES: Record<EngineId, string> = {
  sumatra: "SumatraPDF",
  mupdf: "mupdf latest",
  pdfium: "pdfium",
  pdfjs: "pdf.js",
};

export interface RenderedPage {
  image: CanvasImageSource;
  width: number;
  height: number;
}

export interface CompareEngine {
  // resolves to the page count
  open(bytes: Uint8Array): Promise<number>;
  // page 1-based, rendered as large as fits in dx x dy pixels
  render(pageNo: number, dx: number, dy: number): Promise<RenderedPage>;
  destroy(): void;
}

export interface EngineVersions {
  sumatra: string;
  mupdf: string;
  pdfium: string;
  pdfjs: string;
}

export async function loadEngineVersions(): Promise<EngineVersions> {
  const v: EngineVersions = { sumatra: "", mupdf: "", pdfium: "", pdfjs: "" };
  try {
    const rsp = await fetch("/engines/versions.json", { cache: "no-cache" });
    if (rsp.ok) {
      Object.assign(v, await rsp.json());
    }
  } catch {
    // versions are only shown next to the engine name
  }
  try {
    const rsp = await fetch("/sumatrapdf/version.txt", { cache: "no-cache" });
    if (rsp.ok) {
      v.sumatra = (await rsp.text()).trim().split(/\s+/)[0]?.slice(0, 9) ?? "";
    }
  } catch {
    // same
  }
  return v;
}

async function toImage(width: number, height: number, pixels: ArrayBuffer): Promise<RenderedPage> {
  const data = new ImageData(new Uint8ClampedArray(pixels), width, height);
  return { image: await createImageBitmap(data), width, height };
}

interface WorkerReply {
  id?: number;
  ready?: boolean;
  error?: string;
  pageCount?: number;
  width?: number;
  height?: number;
  pixels?: ArrayBuffer;
}

class WorkerEngine implements CompareEngine {
  private worker: Worker;
  private ready: Promise<void>;
  private nextId = 1;
  private waiting = new Map<number, { resolve: (r: WorkerReply) => void; reject: (e: Error) => void }>();

  constructor(url: string, type: WorkerType) {
    this.worker = new Worker(url, { type });
    this.ready = new Promise((resolve, reject) => {
      this.worker.onerror = (e) => {
        const err = new Error(e.message || `${url} failed to load`);
        reject(err);
        for (const w of this.waiting.values()) {
          w.reject(err);
        }
        this.waiting.clear();
      };
      this.worker.onmessage = (e: MessageEvent<WorkerReply>) => {
        const r = e.data;
        if (r.ready) {
          resolve();
          return;
        }
        const w = r.id === undefined ? undefined : this.waiting.get(r.id);
        if (!w) {
          return;
        }
        this.waiting.delete(r.id!);
        if (r.error) {
          w.reject(new Error(r.error));
          return;
        }
        w.resolve(r);
      };
    });
  }

  private async call(msg: Record<string, unknown>, transfer: Transferable[] = []): Promise<WorkerReply> {
    await this.ready;
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      this.waiting.set(id, { resolve, reject });
      this.worker.postMessage({ id, ...msg }, transfer);
    });
  }

  async open(bytes: Uint8Array): Promise<number> {
    const copy = bytes.slice().buffer;
    const r = await this.call({ type: "open", bytes: copy }, [copy]);
    return r.pageCount ?? 0;
  }

  async render(pageNo: number, dx: number, dy: number): Promise<RenderedPage> {
    const r = await this.call({ type: "render", pageNo, dx, dy });
    return toImage(r.width!, r.height!, r.pixels!);
  }

  destroy() {
    this.worker.terminate();
  }
}

// the parts of SumatraPDF's emscripten Module the render api uses
// (docs/wasm-host-api.md in the sumatrapdf repo)
interface SumatraModule {
  sumatraRenderReady?: boolean;
  sumatraRenderPath?: string;
  FS: { writeFile(path: string, data: Uint8Array): void; unlink(path: string): void };
  HEAPU8: Uint8Array;
  _sumatra_render_open(): number;
  _sumatra_render_page(pageNo: number, dx: number, dy: number): number;
  _sumatra_render_dx(): number;
  _sumatra_render_dy(): number;
  _sumatra_render_close(): void;
}

const SUMATRA_DOC_PATH = "/tmp/compare.pdf";

class SumatraEngine implements CompareEngine {
  private iframe: HTMLIFrameElement;
  private module: Promise<SumatraModule>;

  constructor() {
    const iframe = document.createElement("iframe");
    iframe.src = "/sumatrapdf/?render-api";
    iframe.title = "SumatraPDF renderer";
    iframe.setAttribute("aria-hidden", "true");
    iframe.tabIndex = -1;
    iframe.style.cssText = "position:fixed;left:-10000px;top:0;width:200px;height:200px;border:0;visibility:hidden";
    document.body.appendChild(iframe);
    this.iframe = iframe;
    this.module = new Promise((resolve, reject) => {
      const started = Date.now();
      const poll = () => {
        let m: SumatraModule | undefined;
        try {
          m = (iframe.contentWindow as unknown as { Module?: SumatraModule } | null)?.Module;
        } catch {
          m = undefined;
        }
        if (m?.sumatraRenderReady) {
          resolve(m);
          return;
        }
        if (Date.now() - started > 120_000) {
          reject(new Error("SumatraPDF didn't load"));
          return;
        }
        setTimeout(poll, 100);
      };
      poll();
    });
  }

  async open(bytes: Uint8Array): Promise<number> {
    const m = await this.module;
    m.FS.writeFile(SUMATRA_DOC_PATH, bytes);
    m.sumatraRenderPath = SUMATRA_DOC_PATH;
    const n = m._sumatra_render_open();
    if (n <= 0) {
      throw new Error("SumatraPDF couldn't open the document");
    }
    return n;
  }

  async render(pageNo: number, dx: number, dy: number): Promise<RenderedPage> {
    const m = await this.module;
    const ptr = m._sumatra_render_page(pageNo, dx, dy);
    if (!ptr) {
      throw new Error(`SumatraPDF couldn't render page ${pageNo}`);
    }
    const w = m._sumatra_render_dx();
    const h = m._sumatra_render_dy();
    // HEAPU8 is replaced when memory grows, so it's read after the call
    const pixels = m.HEAPU8.slice(ptr, ptr + w * h * 4);
    return toImage(w, h, pixels.buffer);
  }

  destroy() {
    this.iframe.remove();
  }
}

// pdf.js: the parts of its api used here
interface PdfjsViewport {
  width: number;
  height: number;
}
interface PdfjsPage {
  getViewport(opts: { scale: number }): PdfjsViewport;
  render(opts: { canvas: HTMLCanvasElement; viewport: PdfjsViewport }): { promise: Promise<void> };
  cleanup(): void;
}
interface PdfjsDocument {
  numPages: number;
  getPage(pageNo: number): Promise<PdfjsPage>;
  destroy(): Promise<void>;
}
interface PdfjsLib {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument(opts: Record<string, unknown>): { promise: Promise<PdfjsDocument> };
}

const PDFJS_BASE = "/engines/pdfjs/";
let pdfjsLib: Promise<PdfjsLib> | null = null;

function loadPdfjs(): Promise<PdfjsLib> {
  if (!pdfjsLib) {
    const url = PDFJS_BASE + "pdf.min.mjs";
    pdfjsLib = (import(/* @vite-ignore */ url) as Promise<PdfjsLib>).then((lib) => {
      lib.GlobalWorkerOptions.workerSrc = PDFJS_BASE + "pdf.worker.min.mjs";
      return lib;
    });
  }
  return pdfjsLib;
}

class PdfjsEngine implements CompareEngine {
  private doc: PdfjsDocument | null = null;

  async open(bytes: Uint8Array): Promise<number> {
    const lib = await loadPdfjs();
    if (this.doc) {
      void this.doc.destroy();
    }
    this.doc = await lib.getDocument({
      // pdf.js transfers the buffer to its worker
      data: bytes.slice(),
      cMapUrl: PDFJS_BASE + "cmaps/",
      cMapPacked: true,
      standardFontDataUrl: PDFJS_BASE + "standard_fonts/",
      wasmUrl: PDFJS_BASE + "wasm/",
      iccUrl: PDFJS_BASE + "iccs/",
    }).promise;
    return this.doc.numPages;
  }

  async render(pageNo: number, dx: number, dy: number): Promise<RenderedPage> {
    if (!this.doc) {
      throw new Error("no document");
    }
    const page = await this.doc.getPage(pageNo);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(dx / base.width, dy / base.height);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    await page.render({ canvas, viewport }).promise;
    page.cleanup();
    return { image: canvas, width: canvas.width, height: canvas.height };
  }

  destroy() {
    if (this.doc) {
      void this.doc.destroy();
    }
    this.doc = null;
  }
}

export function createEngine(id: EngineId): CompareEngine {
  switch (id) {
    case "sumatra":
      return new SumatraEngine();
    case "mupdf":
      return new WorkerEngine("/compare/mupdf-worker.js", "module");
    case "pdfium":
      return new WorkerEngine("/compare/pdfium-worker.js", "classic");
    case "pdfjs":
      return new PdfjsEngine();
  }
}
